import React, { useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { NavigateNext, Search } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { showNotification } from '../store/notificationSlice';
import { useAppDispatch, useAppSelector } from '../store';
import { apiService } from '../services/api';
import type { LeaveBalance as LeaveBalanceType } from '../types/leave';

const LeaveBalance: React.FC = () => {
  const dispatch = useAppDispatch();
  const leaveTypes = useAppSelector((s) => s.leaveTypes.leaveTypes).filter((t) => t.isActive);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [balance, setBalance] = useState<LeaveBalanceType | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Auto-select first active leave type
  if (leaveTypes.length > 0 && !selectedTypeId) {
    setSelectedTypeId(leaveTypes[0].id);
  }

  const fetchBalance = async () => {
    if (!employeeId.trim() || !selectedTypeId) return;
    setBalanceLoading(true);
    setBalance(null);
    try {
      const res = await apiService.getLeaveBalance(employeeId.trim(), selectedTypeId);
      setBalance(res.data);
    } catch (err: any) {
      dispatch(showNotification({ message: err.message ?? 'Failed to fetch balance', severity: 'error' }));
    } finally {
      setBalanceLoading(false);
    }
  };

  const selectedTypeName = leaveTypes.find((t) => t.id === selectedTypeId)?.name ?? '';

  return (
    <DashboardLayout>
      <Box>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Leave Management</Typography>
          <Typography variant="body2" color="text.primary" fontWeight={600}>Leave Balance</Typography>
        </Breadcrumbs>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Leave Balance</Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
          <TextField
            label="Employee ID"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            size="small"
            sx={{ minWidth: 280 }}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchBalance(); }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Leave Type</InputLabel>
            <Select label="Leave Type" value={selectedTypeId} onChange={(e) => setSelectedTypeId(e.target.value)}>
              {leaveTypes.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={balanceLoading ? <CircularProgress size={16} /> : <Search />}
            onClick={fetchBalance}
            disabled={balanceLoading || !employeeId.trim() || !selectedTypeId}
          >
            Check
          </Button>
        </Box>

        {balance && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 500 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>{balance.employeeId}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>{selectedTypeName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Balance</TableCell>
                  <TableCell>
                    <Typography
                      variant="h6"
                      color={balance.balance >= 0 ? 'success.main' : 'error.main'}
                      fontWeight={700}
                    >
                      {balance.balance}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default LeaveBalance;
